import { useState, useEffect } from "react";
import {
  Menu,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useQueryMutation from "hooks/useQueryMutation";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useFetch from "hooks/useFetch";
import ShareProjectMenuItem from "./ShareProjectMenuItem";

export default function MutateProjectMenu({ project }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [askForOverrideDelete, setAskForOverrideDelete] = useState(false);

  const { mutate: deleteProject } = useQueryMutation(
    `/project/${project.id}`,
    "DELETE"
  );
  const { mutate: deleteProjectOverride, isPending: isDeletingOverride } =
    useQueryMutation(`/project/${project.id}?override=true`, "DELETE");

  const { mutate: updateProject, isPending: isUpdating } = useQueryMutation(
    `/project/${project.id}`,
    "PATCH"
  );
  const { data: customerRes } = useFetch("/customer");

  const [anchorEl, setAnchorEl] = useState(null);
  const [action, setAction] = useState("");
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState(0);

  const menuOpen = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const closeDialog = () => {
    setAction("");
    setAskForOverrideDelete(false);
  };

  useEffect(() => {
    setName(project?.name || "");
    setCustomerId(project?.customer_id || 0);
  }, [project]);

  const archiveDone = () => {
    setAskForOverrideDelete(false);
    queryClient.invalidateQueries({ queryKey: ["/project"] });
    closeDialog();
    navigate("/");
  };

  const handleArchive = () => {
    deleteProject(
      {},
      {
        onSuccess: (data) => {
          if (data.status === 400) {
            setAskForOverrideDelete(true);
            return;
          }
          archiveDone();
        },
      }
    );
  };

  const handleArchiveOverride = () => {
    deleteProjectOverride(
      {},
      {
        onSuccess: archiveDone,
      }
    );
  };

  const handleUpdate = () => {
    const payload =
      action === "edit"
        ? { name }
        : { customer_id: customerId, name: project.name };

    updateProject(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["/project"],
        });
        closeDialog();
      },
    });
  };

  const handleActionChange = (action) => () => {
    if (action === "archive") {
      handleArchive();
    } else {
      setAction(action);
    }
    handleClose();
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Group settings">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={menuOpen ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="project-menu"
        open={menuOpen}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleActionChange("edit")}>Edit Project</MenuItem>
        <MenuItem onClick={handleActionChange("change-customer")}>
          Change Project Group
        </MenuItem>
        <MenuItem onClick={handleActionChange("archive")}>
          Archive Project
        </MenuItem>
        <ShareProjectMenuItem project={project} onClick={handleClose} />
      </Menu>

      <Dialog
        open={askForOverrideDelete}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Archive Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography color="error" component="span">
              There are simulations in this project, are you sure you want to
              archive?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleArchiveOverride} disabled={isDeletingOverride}>
            {isDeletingOverride ? "Archiving..." : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={action === "change-customer"}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Project Group</DialogTitle>
        <DialogContent>
          <FormControl variant="standard" sx={{ width: "100%" }}>
            <Select
              labelId="group-select-label"
              id="group-select"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              label="Select group"
            >
              <MenuItem value={0}>
                <em>Select group</em>
              </MenuItem>
              {(customerRes?.customers || []).map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleUpdate}
            disabled={isUpdating || customerId === 0}
          >
            {isUpdating ? "Updating..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={action === "edit"}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Project Name"
            type="text"
            fullWidth
            variant="standard"
            value={name}
            required
            maxLength={32}
            disabled={isUpdating || !name.trim()}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={isUpdating || !name.trim()}>
            {isUpdating ? "Updating..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
